import { formatDate, isVideoTimeRange } from '@/utils/date';
import { formatVideoTime } from '@/utils/time';
import { getReportEventTime } from '@/utils/report-event-time';
import { fetchImageBlob } from '@/services/image-blob';
import type { ReportEvent, ReportWithEvents } from '@/types/report';
import type { ReportGroupByMode } from '../constants';
import {
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    EVENT_DATA_TABLE_BODY_BOTTOM_PAD,
    EVENT_DATA_TABLE_BODY_TOP_GAP,
    EVENT_DATA_TABLE_HEADER_HEIGHT,
    EVENT_DATA_TABLE_SOURCE_TYPE_GAP,
    EVENT_SECONDARY_THUMBS_BOTTOM_PAD,
    EVENT_SECONDARY_THUMBS_TOP_GAP,
    layoutReportExportSecondaryThumbnailsRow,
    EVENT_ROW_GAP,
    EVENT_TABLE_GAP,
    LEFT_COL_RATIO,
    MAIN_THUMB_HEIGHT,
    PAGE_PADDING_X,
    PAGE_PADDING_Y,
    PROMPT_BLOCK_TAIL_GAP,
    REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
    SUMMARY_COL_GAP,
    SUMMARY_INNER_PAD,
    SUMMARY_ROW_GAP,
    TEXT,
} from './reportExportShared';
import { groupReportEvents } from './groupReportEvents';

const LOGO_PATH = '/images/conntour_logo_white.svg';
const UNTITLED_PROMPT_LABEL = 'Untitled prompt';
const UNKNOWN_SOURCE_LABEL = 'Unknown source';

export const EVENT_DESCRIPTION_MAX_LINES = 4;

export interface PreparedEvent extends ReportEvent {
    thumbnailImage: HTMLImageElement | null;
    secondaryImages: (HTMLImageElement | null)[];
}

export interface ReportExportPromptGroup<T extends ReportEvent = ReportEvent> {
    promptIndex: number;
    promptText: string;
    events: T[];
    headingLabel: string;
    showHeader: boolean;
    groupBy: ReportGroupByMode;
}

type ReportExportPromptGroupContent<T extends ReportEvent> = Omit<
    ReportExportPromptGroup<T>,
    'groupBy'
>;

export type PromptPdfMeta = ReportExportPromptGroup<PreparedEvent>;

export interface PdfLayoutRow {
    meta: PromptPdfMeta;
    event: PreparedEvent;
    isFirstInPrompt: boolean;
    showPromptHeader: boolean;
}

const resolveShowPromptHeader = (
    row: PdfLayoutRow,
    rowIndexOnPage: number,
    lastRowBeforePage: PdfLayoutRow | undefined,
): boolean =>
    row.meta.showHeader &&
    (row.isFirstInPrompt ||
        (rowIndexOnPage === 0 &&
            lastRowBeforePage !== undefined &&
            lastRowBeforePage.meta.promptIndex === row.meta.promptIndex));

export interface ReportExportSummaryCell {
    label: string;
    value: string;
}

// Grouped through the same `groupReportEvents` the events list uses, so the
// export's groups carry the same membership and the same group order the user
// is looking at. Only the heading wording differs per mode.
const groupReportEventsWithHeaders = <T extends ReportEvent>(
    events: T[],
    mode: Exclude<ReportGroupByMode, 'none'>,
): ReportExportPromptGroupContent<T>[] => {
    const headingPrefix = mode === 'source' ? 'Source' : 'Group';
    const fallbackLabel =
        mode === 'source' ? UNKNOWN_SOURCE_LABEL : UNTITLED_PROMPT_LABEL;
    return groupReportEvents(events, mode).map((group, index) => ({
        promptIndex: index + 1,
        promptText: group.sectionLabel ?? fallbackLabel,
        events: group.events,
        headingLabel: `${headingPrefix} ${index + 1}`,
        showHeader: true,
    }));
};

const groupReportEventsWithoutHeader = <T extends ReportEvent>(
    events: T[],
): ReportExportPromptGroupContent<T>[] => [
    {
        promptIndex: 1,
        promptText: 'Events',
        events,
        headingLabel: '',
        showHeader: false,
    },
];

const buildPromptGroupContents = <T extends ReportEvent>(
    events: T[],
    groupBy: ReportGroupByMode,
): ReportExportPromptGroupContent<T>[] =>
    groupBy === 'none'
        ? groupReportEventsWithoutHeader(events)
        : groupReportEventsWithHeaders(events, groupBy);

export const groupReportEventsForExport = <T extends ReportEvent>(
    events: T[],
    groupBy: ReportGroupByMode = 'prompt',
): ReportExportPromptGroup<T>[] =>
    buildPromptGroupContents(events, groupBy).map((content) => ({
        ...content,
        groupBy,
    }));

const loadText = async (path: string): Promise<string> => {
    const response = await fetch(path, { credentials: 'include' });
    if (!response.ok) {
        throw new Error(`Failed to load ${path}`);
    }
    return response.text();
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }
            reject(new Error('Failed to read blob'));
        };
        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
    });

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = src;
    });

const fetchImage = async (url: string): Promise<HTMLImageElement | null> => {
    try {
        const blob = await fetchImageBlob(url);
        const dataUrl = await blobToDataUrl(blob);
        return await loadImage(dataUrl);
    } catch {
        return null;
    }
};

const loadLogoImage = async (): Promise<HTMLImageElement> => {
    const svg = await loadText(LOGO_PATH);
    const blackSvg = svg
        .replace(/#fff/gi, TEXT)
        .replace(/#ffffff/gi, TEXT)
        .replace(/fill:\s*#fff/gi, `fill:${TEXT}`)
        .replace(/fill:\s*#ffffff/gi, `fill:${TEXT}`);
    const blob = new Blob([blackSvg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    try {
        return await loadImage(url);
    } finally {
        URL.revokeObjectURL(url);
    }
};

const LOGO_DOCX_HEIGHT_PX = 44;
const LOGO_RASTER_HEIGHT_PX = 176;

const resolveReportExportLogoImage = async (
    organizationLogoUrl?: string,
): Promise<HTMLImageElement> => {
    if (organizationLogoUrl) {
        const organizationLogo = await fetchImage(organizationLogoUrl);
        if (organizationLogo) {
            return organizationLogo;
        }
    }
    return loadLogoImage();
};

export interface ReportExportLogoPayload {
    data: Uint8Array;
    transformWidth: number;
    transformHeight: number;
}

export const loadReportExportLogoPngForDocx = async (
    organizationLogoUrl?: string,
): Promise<ReportExportLogoPayload | null> => {
    try {
        const logo = await resolveReportExportLogoImage(organizationLogoUrl);
        const intrinsicWidth = logo.naturalWidth || logo.width;
        const intrinsicHeight = logo.naturalHeight || logo.height;
        if (!intrinsicWidth || !intrinsicHeight) {
            return null;
        }
        const rasterHeight = LOGO_RASTER_HEIGHT_PX;
        const rasterWidth = Math.max(
            1,
            Math.round((intrinsicWidth / intrinsicHeight) * rasterHeight),
        );
        const canvas = document.createElement('canvas');
        canvas.width = rasterWidth;
        canvas.height = rasterHeight;
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }
        context.drawImage(logo, 0, 0, rasterWidth, rasterHeight);
        const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((value) => resolve(value), 'image/png');
        });
        if (!blob) {
            return null;
        }
        const data = new Uint8Array(await blob.arrayBuffer());
        const scale = LOGO_DOCX_HEIGHT_PX / rasterHeight;
        const transformWidth = Math.max(1, Math.round(rasterWidth * scale));
        const transformHeight = LOGO_DOCX_HEIGHT_PX;
        return { data, transformWidth, transformHeight };
    } catch {
        return null;
    }
};

export const buildReportExportGroupSummaryParts = (
    events: ReportEvent[],
): string[] => {
    const thumbnailCount = events.filter(
        (event) => event.thumbnailUrls[0],
    ).length;
    return [`${events.length} events`, `${thumbnailCount} thumbnails`];
};

export const formatReportExportUtcTimestamp = (seconds: number): string =>
    `${new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'UTC',
    }).format(new Date(seconds * 1000))} UTC`;

export const formatReportExportEventTimestamp = (
    event: ReportEvent,
): string => {
    const startSeconds = getReportEventTime(event);
    if (startSeconds === undefined) {
        return formatReportExportUtcTimestamp(event.createdAt);
    }
    if (isVideoTimeRange(event.sourceType)) {
        return formatVideoTime(startSeconds);
    }
    return formatReportExportUtcTimestamp(startSeconds);
};

const wrapTextPerLineWidths = (
    context: CanvasRenderingContext2D,
    text: string,
    getMaxWidth: (lineIndex: number) => number,
): string[] => {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return [''];
    }
    const words = normalized.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    const maxWidth = (): number => getMaxWidth(lines.length);

    for (const word of words) {
        let remainder = word;
        while (remainder.length > 0) {
            const candidate = currentLine
                ? `${currentLine} ${remainder}`
                : remainder;
            if (context.measureText(candidate).width <= maxWidth()) {
                currentLine = candidate;
                remainder = '';
                continue;
            }

            if (currentLine) {
                lines.push(currentLine);
                currentLine = '';
                continue;
            }

            const limit = maxWidth();
            let piece = '';
            for (const char of remainder) {
                const next = piece + char;
                if (context.measureText(next).width <= limit) {
                    piece = next;
                } else {
                    break;
                }
            }
            if (piece.length === 0) {
                piece = remainder.charAt(0);
            }
            lines.push(piece);
            remainder = remainder.slice(piece.length);
        }
    }

    if (currentLine) {
        lines.push(currentLine);
    }

    return lines;
};

const wrapText = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): string[] => {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return ['—'];
    }

    const words = normalized.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (context.measureText(candidate).width <= maxWidth) {
            currentLine = candidate;
            continue;
        }

        if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
            continue;
        }

        let fragment = '';
        for (const char of word) {
            const next = `${fragment}${char}`;
            if (context.measureText(next).width > maxWidth && fragment) {
                lines.push(fragment);
                fragment = char;
            } else {
                fragment = next;
            }
        }
        currentLine = fragment;
    }

    if (currentLine) {
        lines.push(currentLine);
    }

    return lines;
};

const buildLayoutRows = (
    events: PreparedEvent[],
    groupBy: ReportGroupByMode,
): PdfLayoutRow[] =>
    // Every row of a group shares one `meta`, so `promptIndex` identifies the
    // group a page break landed in when the header has to be re-printed.
    groupReportEventsForExport(events, groupBy).flatMap((meta) =>
        meta.events.map((event, index) => ({
            meta,
            event,
            isFirstInPrompt: index === 0,
            showPromptHeader: false,
        })),
    );

const assignPromptHeaderFlags = (pages: PdfLayoutRow[][]): void => {
    pages.forEach((page, pageIndex) => {
        page.forEach((row, rowIndex) => {
            const lastBeforePage =
                rowIndex === 0 && pageIndex > 0
                    ? pages[pageIndex - 1][pages[pageIndex - 1].length - 1]
                    : undefined;
            row.showPromptHeader = resolveShowPromptHeader(
                row,
                rowIndex,
                lastBeforePage,
            );
        });
    });
};

export const resolveReportExportSourceLabel = (event: ReportEvent): string => {
    const fromName = event.sourceName?.trim();
    if (fromName) {
        return fromName;
    }
    const fromId = event.sourceId?.trim();
    if (fromId) {
        return fromId;
    }
    return '';
};

export interface ReportExportEventContextColumn {
    label: string;
    value: string;
    detail: string;
}

export const resolveReportExportEventContextColumn = (
    event: ReportEvent,
    groupBy: ReportGroupByMode,
): ReportExportEventContextColumn => {
    if (groupBy !== 'prompt') {
        const promptText = event.promptText.trim();
        return {
            label: 'Prompt',
            value: promptText || 'Untitled prompt',
            detail: '',
        };
    }

    return {
        label: 'Source',
        value: resolveReportExportSourceLabel(event) || 'Unknown',
        detail: event.sourceType,
    };
};

export const buildReportExportSummaryGrid = (
    report: ReportWithEvents,
    createdByLabel: string,
): ReportExportSummaryCell[][] => {
    const sourceNames = Array.from(
        new Set(
            report.events
                .map((event) => resolveReportExportSourceLabel(event))
                .filter(Boolean),
        ),
    );
    const sourcesValue = sourceNames.length > 0 ? sourceNames.join(', ') : '—';
    const promptGroupCount = groupReportEvents(report.events, 'prompt').length;

    return [
        [
            { label: 'STATUS', value: report.status },
            { label: 'SOURCES', value: sourcesValue },
            { label: 'EVENTS', value: String(report.events.length) },
        ],
        [
            { label: 'EVENT GROUPS', value: String(promptGroupCount) },
            {
                label: 'CREATED',
                value: formatReportExportUtcTimestamp(report.createdAt),
            },
            { label: 'CREATED BY', value: createdByLabel },
        ],
    ];
};

const measureSummaryGridHeight = (
    context: CanvasRenderingContext2D,
    report: ReportWithEvents,
    panelInnerWidth: number,
    createdByLabel: string,
): number => {
    const colWidth = (panelInnerWidth - SUMMARY_COL_GAP * 2) / 3;
    const grid = buildReportExportSummaryGrid(report, createdByLabel);
    context.font = '600 20px Arial, sans-serif';

    const rowHeights = grid.map((row) => {
        const cellHeights = row.map((cell) => {
            context.font = '600 20px Arial, sans-serif';
            const labelBlock = 22;
            context.font = '600 24px Arial, sans-serif';
            const valueLines = wrapText(
                context,
                cell.value,
                colWidth - 4,
            ).length;
            const valueBlock = Math.max(1, valueLines) * 32;
            return labelBlock + 10 + valueBlock + SUMMARY_ROW_GAP;
        });
        return Math.max(...cellHeights);
    });

    return SUMMARY_INNER_PAD * 2 + rowHeights[0] + rowHeights[1];
};

export const measureReportExportSummaryPanelHeight = (
    report: ReportWithEvents,
    panelInnerWidth: number,
    createdByLabel: string,
): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return 200;
    }
    return measureSummaryGridHeight(
        context,
        report,
        panelInnerWidth,
        createdByLabel,
    );
};

const computeSummaryTopY = (): number => PAGE_PADDING_Y + 88;

const measureReportExportMultilineRows = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): number =>
    text
        .split('\n')
        .reduce(
            (total, line) => total + wrapText(context, line, maxWidth).length,
            0,
        );

const measureSummaryEndY = (
    context: CanvasRenderingContext2D,
    report: ReportWithEvents,
    createdByLabel: string,
): number => {
    let y = computeSummaryTopY();
    context.font = '700 56px Georgia, serif';
    y += drawMeasure(
        context,
        report.name,
        CANVAS_WIDTH - PAGE_PADDING_X * 2,
        64,
    );
    y += 12;
    context.font = '400 30px Georgia, serif';
    const subtitle = report.description?.trim() || report.name;
    y +=
        measureReportExportMultilineRows(
            context,
            subtitle,
            CANVAS_WIDTH - PAGE_PADDING_X * 2,
        ) * 38;
    y += 36;
    const panelWidth = CANVAS_WIDTH - PAGE_PADDING_X * 2;
    y += measureSummaryGridHeight(
        context,
        report,
        panelWidth - SUMMARY_INNER_PAD * 2,
        createdByLabel,
    );
    return y;
};

const drawMeasure = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    lineHeight: number,
): number => wrapText(context, text, maxWidth).length * lineHeight;

const estimatePromptHeaderHeight = (
    context: CanvasRenderingContext2D,
    meta: PromptPdfMeta,
    width: number,
): number => {
    if (!meta.showHeader) {
        return 0;
    }
    let height = 8;
    const prefix = meta.headingLabel;
    context.font = '700 28px Georgia, serif';
    const prefixWidth = context.measureText(prefix).width;
    const gap = 16;
    const firstLineWidth = Math.max(48, width - prefixWidth - gap);
    const bodyText =
        (meta.promptText || 'Untitled prompt').trim() || 'Untitled prompt';
    context.font = '400 40px Georgia, serif';
    const bodyLines = wrapTextPerLineWidths(context, bodyText, (lineIndex) =>
        lineIndex === 0 ? firstLineWidth : width,
    ).length;
    height += Math.max(44, bodyLines * 46);
    height += 14;
    context.font = '400 22px Arial, sans-serif';
    height += 26;
    height += PROMPT_BLOCK_TAIL_GAP;
    return height;
};

export const measureReportExportPromptHeaderHeight = (
    meta: PromptPdfMeta,
    width: number,
): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return 120;
    }
    return estimatePromptHeaderHeight(context, meta, width);
};

const descriptionLineCount = (
    context: CanvasRenderingContext2D,
    description: string,
    maxWidth: number,
): number => {
    context.font = '400 22px Arial, sans-serif';
    return Math.min(
        wrapText(context, description, maxWidth).length,
        EVENT_DESCRIPTION_MAX_LINES,
    );
};

export const measureReportExportDescriptionLineCount = (
    description: string,
    maxWidth: number,
): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return EVENT_DESCRIPTION_MAX_LINES;
    }
    return descriptionLineCount(context, description, maxWidth);
};

const measureEventDataTableTextColumnsHeight = (
    context: CanvasRenderingContext2D,
    event: PreparedEvent,
    width: number,
    groupBy: ReportGroupByMode,
): number => {
    const colEvent = width * 0.55;
    const colSource = width * 0.45;
    const paddingX = 16;
    const contextColumn = resolveReportExportEventContextColumn(event, groupBy);
    const description = event.description?.trim();

    context.font = '400 26px Arial, sans-serif';
    const timestampLines = wrapText(
        context,
        formatReportExportEventTimestamp(event),
        colEvent - paddingX * 2,
    ).length;
    const descriptionLines = description
        ? descriptionLineCount(context, description, colEvent - paddingX * 2)
        : 0;
    const descriptionHeight = descriptionLines ? 8 + descriptionLines * 28 : 0;
    const eventColumnHeight = timestampLines * 30 + descriptionHeight + 20;

    context.font = '400 24px Arial, sans-serif';
    const sourceLines = wrapText(
        context,
        contextColumn.value,
        colSource - paddingX * 2,
    ).length;
    const sourceColumnHeight = contextColumn.detail
        ? sourceLines * 28 + EVENT_DATA_TABLE_SOURCE_TYPE_GAP + 22
        : sourceLines * 28;

    return Math.max(eventColumnHeight, sourceColumnHeight);
};

export const measureReportExportEventDataTextColumnsHeight = (
    event: PreparedEvent,
    tableWidth: number,
    groupBy: ReportGroupByMode,
): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return 120;
    }
    return measureEventDataTableTextColumnsHeight(
        context,
        event,
        tableWidth,
        groupBy,
    );
};

export const measureReportExportSecondaryThumbnailsStripHeight = (
    thumbnailFramesLength: number,
    panelWidth: number,
): number => {
    if (thumbnailFramesLength <= 1) {
        return 0;
    }
    const thumbCount = Math.min(
        REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
        thumbnailFramesLength - 1,
    );
    const { cellHeight } = layoutReportExportSecondaryThumbnailsRow(
        panelWidth,
        thumbCount,
    );
    return (
        EVENT_SECONDARY_THUMBS_TOP_GAP +
        cellHeight +
        EVENT_SECONDARY_THUMBS_BOTTOM_PAD
    );
};

const measureEventDataTableBodyHeight = (
    context: CanvasRenderingContext2D,
    event: PreparedEvent,
    width: number,
    groupBy: ReportGroupByMode,
): number => {
    const textColumns = measureEventDataTableTextColumnsHeight(
        context,
        event,
        width,
        groupBy,
    );
    const secondaryStrip = measureReportExportSecondaryThumbnailsStripHeight(
        event.thumbnailFrames?.length ?? 0,
        width,
    );
    return textColumns + secondaryStrip;
};

const computeEventDataTableHeight = (
    context: CanvasRenderingContext2D,
    event: PreparedEvent,
    tableWidth: number,
    groupBy: ReportGroupByMode,
): number => {
    const bodyContentHeight = measureEventDataTableBodyHeight(
        context,
        event,
        tableWidth,
        groupBy,
    );
    const intrinsicHeight =
        EVENT_DATA_TABLE_HEADER_HEIGHT +
        EVENT_DATA_TABLE_BODY_TOP_GAP +
        bodyContentHeight +
        EVENT_DATA_TABLE_BODY_BOTTOM_PAD;
    return Math.max(MAIN_THUMB_HEIGHT + 52, 200, intrinsicHeight);
};

export const measureReportExportEventTableHeight = (
    event: PreparedEvent,
    tableWidth: number,
    groupBy: ReportGroupByMode,
): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        return Math.max(MAIN_THUMB_HEIGHT + 52, 200);
    }
    return computeEventDataTableHeight(context, event, tableWidth, groupBy);
};

const estimateEventRowHeight = (
    context: CanvasRenderingContext2D,
    event: PreparedEvent,
    leftWidth: number,
    groupBy: ReportGroupByMode,
): number => {
    const tableHeight = computeEventDataTableHeight(
        context,
        event,
        leftWidth,
        groupBy,
    );
    return EVENT_ROW_GAP + tableHeight;
};

const estimateRowTotalHeight = (
    context: CanvasRenderingContext2D,
    row: PdfLayoutRow,
    rowWidth: number,
    showPromptHeader: boolean,
    groupBy: ReportGroupByMode,
): number => {
    let height = 0;
    if (showPromptHeader) {
        height += estimatePromptHeaderHeight(context, row.meta, rowWidth);
    }
    const leftWidth = rowWidth * LEFT_COL_RATIO - EVENT_TABLE_GAP / 2;
    height += estimateEventRowHeight(context, row.event, leftWidth, groupBy);
    return height;
};

const prepareEvents = async (events: ReportEvent[]): Promise<PreparedEvent[]> =>
    Promise.all(
        events.map(async (event) => {
            const primaryUrl = event.thumbnailUrls[0];
            const thumbnailImage = primaryUrl
                ? await fetchImage(primaryUrl)
                : null;
            const secondaryUrls = event.thumbnailUrls.slice(
                1,
                1 + REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
            );
            const secondaryImages = await Promise.all(
                secondaryUrls.map((url) => fetchImage(url)),
            );
            return { ...event, thumbnailImage, secondaryImages };
        }),
    );

const paginateLayoutRows = (
    rows: PdfLayoutRow[],
    report: ReportWithEvents,
    createdByLabel: string,
    groupBy: ReportGroupByMode,
): PdfLayoutRow[][] => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to create pagination canvas');
    }

    const pages: PdfLayoutRow[][] = [];
    let current: PdfLayoutRow[] = [];
    const rowWidth = CANVAS_WIDTH - PAGE_PADDING_X * 2;

    const firstPageStart =
        measureSummaryEndY(context, report, createdByLabel) + 56;
    const subsequentPageStart = PAGE_PADDING_Y + 120;
    const bottomReserve = 100;

    let remaining = CANVAS_HEIGHT - firstPageStart - bottomReserve;

    for (const row of rows) {
        const measureRowOnCurrentPage = (): number => {
            const lastRowBeforePage =
                current.length === 0 && pages.length > 0
                    ? pages[pages.length - 1][
                          pages[pages.length - 1].length - 1
                      ]
                    : undefined;
            const showPromptHeader = resolveShowPromptHeader(
                row,
                current.length,
                lastRowBeforePage,
            );
            return estimateRowTotalHeight(
                context,
                row,
                rowWidth,
                showPromptHeader,
                groupBy,
            );
        };

        let needed = measureRowOnCurrentPage();
        if (current.length > 0 && needed > remaining) {
            pages.push(current);
            current = [];
            remaining = CANVAS_HEIGHT - subsequentPageStart - bottomReserve;
        }

        needed = measureRowOnCurrentPage();
        current.push(row);
        remaining -= needed;
    }

    if (current.length > 0 || rows.length === 0) {
        pages.push(current);
    }

    assignPromptHeaderFlags(pages);
    return pages;
};

export const resolveReportExportCreatedByLabel = (
    report: ReportWithEvents,
    viewerUserId: string | undefined,
    viewerDisplayName: string | undefined,
): string => {
    const trimmed = viewerDisplayName?.trim();
    if (
        trimmed &&
        viewerUserId &&
        report.userId &&
        report.userId === viewerUserId
    ) {
        return trimmed;
    }
    return report.userId ? report.userId.toUpperCase() : '—';
};

export interface ReportExportPaginationBundle {
    pageRowSets: PdfLayoutRow[][];
    createdByLabel: string;
    generatedAt: string;
    preparedEvents: PreparedEvent[];
}

export const buildReportExportPagination = async (
    report: ReportWithEvents,
    viewerUserId: string | undefined,
    viewerDisplayName?: string,
    groupBy: ReportGroupByMode = 'prompt',
): Promise<ReportExportPaginationBundle> => {
    const generatedAt = formatDate(Math.floor(Date.now() / 1000));
    const createdByLabel = resolveReportExportCreatedByLabel(
        report,
        viewerUserId,
        viewerDisplayName,
    );
    const preparedEvents = await prepareEvents(report.events);
    const layoutRows = buildLayoutRows(preparedEvents, groupBy);
    const pageRowSets = paginateLayoutRows(
        layoutRows,
        report,
        createdByLabel,
        groupBy,
    );
    return { pageRowSets, createdByLabel, generatedAt, preparedEvents };
};
